import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from '@/store/languageStore';
import { useAsyncData } from '@/hooks';
import { pipelineService } from '@/services/crm/pipelineService';
import { ROUTES } from '@/constants/routes';
import type {
  AnalysisSummary,
  MatchStatus,
  PipelineAttachment,
  PipelineCase,
  PipelineRequirement,
  PipelineValueLine,
  ValueSummary,
} from '@/types/pipeline';
import {
  ArrowLeftIcon,
  DocumentArrowUpIcon,
  SparklesIcon,
  CurrencyDollarIcon,
  ClipboardDocumentListIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';

const statusColors: Record<string, string> = {
  fulfilled: 'bg-green-100 text-green-800',
  partial: 'bg-yellow-100 text-yellow-800',
  missing: 'bg-red-100 text-red-800',
  unknown: 'bg-gray-100 text-gray-800',
  review: 'bg-purple-100 text-purple-800',
};

const handlingColors: Record<string, string> = {
  hanterat: 'bg-green-100 text-green-800',
  utveckling: 'bg-blue-100 text-blue-800',
  juridik: 'bg-purple-100 text-purple-800',
};

const HANDLING_OPTIONS = ['hanterat', 'utveckling', 'juridik'] as const;
const MATCH_OPTIONS = ['fulfilled', 'partial', 'missing', 'review', 'unknown'] as const;

type HandlingDraft = {
  handling_status: string;
  manual_comment: string;
  match_status: MatchStatus;
  reuse_in_other_cases: boolean;
};

const formatCurrency = (value: number | null | undefined, currency = 'SEK') =>
  value == null ? '–' : new Intl.NumberFormat('sv-SE', { style: 'currency', currency, maximumFractionDigits: 0 }).format(value);

type Tab = 'overview' | 'documents' | 'requirements' | 'value';
const ANALYSIS_POLL_MS = 5000;

export default function PipelineCaseDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const caseId = parseInt(id || '0');
  const [tab, setTab] = useState<Tab>('overview');
  const [analysisPollMs, setAnalysisPollMs] = useState<number | undefined>(undefined);
  const [selectedDocIds, setSelectedDocIds] = useState<number[]>([]);
  const [expandedReqIds, setExpandedReqIds] = useState<number[]>([]);
  const [handlingDrafts, setHandlingDrafts] = useState<Record<number, HandlingDraft>>({});
  const [savingReqId, setSavingReqId] = useState<number | null>(null);
  const [uploading, setUploading] = useState(false);
  const [submittingAnalysis, setSubmittingAnalysis] = useState(false);
  const [message, setMessage] = useState('');
  const prevAnalysisStatusRef = useRef<string | undefined>();

  const { data: pipelineCase, refetch: refetchCase } = useAsyncData<PipelineCase>(
    caseId ? `/api/crm/pipeline/cases/${caseId}` : null,
    { skip: !caseId, refreshInterval: analysisPollMs }
  );

  const analysisRunning = pipelineCase?.analysis_status === 'analyzing';

  const { data: attachments, refetch: refetchAttachments } = useAsyncData<PipelineAttachment[]>(
    caseId ? `/api/crm/pipeline/cases/${caseId}/attachments` : null,
    { skip: !caseId, initialData: [] }
  );

  const { data: reqData, refetch: refetchReqs } = useAsyncData<{ requirements: PipelineRequirement[]; summary: AnalysisSummary }>(
    caseId ? `/api/crm/pipeline/cases/${caseId}/requirements` : null,
    { skip: !caseId, refreshInterval: analysisPollMs, initialData: { requirements: [], summary: { total: 0, fulfilled: 0, partial: 0, missing: 0, review: 0, ska_total: 0, bor_total: 0 } } }
  );

  useEffect(() => {
    if (pipelineCase?.analysis_status === 'analyzing') {
      setAnalysisPollMs(ANALYSIS_POLL_MS);
      return;
    }
    setAnalysisPollMs(undefined);
  }, [pipelineCase?.analysis_status]);

  useEffect(() => {
    const prev = prevAnalysisStatusRef.current;
    const curr = pipelineCase?.analysis_status;
    prevAnalysisStatusRef.current = curr;

    if (prev === 'analyzing' && curr === 'completed') {
      void refetchReqs();
      setMessage(t('pipeline.analyzeSuccess'));
    } else if (prev === 'analyzing' && curr === 'failed') {
      const reason = pipelineCase?.analysis_error_message;
      setMessage(reason ? `${t('pipeline.analyzeFailed')}: ${reason}` : t('pipeline.analyzeFailed'));
      void refetchReqs();
    }
  }, [pipelineCase?.analysis_status, pipelineCase?.analysis_error_message, refetchReqs, t]);

  const { data: valueSummary, refetch: refetchValue } = useAsyncData<ValueSummary>(
    caseId ? `/api/crm/pipeline/cases/${caseId}/value-lines` : null,
    { skip: !caseId }
  );

  const { data: products } = useAsyncData<{ id: string; name: string; code: string; price: number; pricing_type: string; unit: string }[]>(
    '/api/pricelist/products',
    { initialData: [], transform: (d) => (Array.isArray(d) ? d : d?.products || []) }
  );

  const [selectedProduct, setSelectedProduct] = useState('');
  const [quantity, setQuantity] = useState('1');

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0 || !caseId) return;
    setUploading(true);
    try {
      const { uploaded, failed } = await pipelineService.uploadAttachments(caseId, files, 'kravspec');
      refetchAttachments();
      if (failed.length === 0) {
        setMessage(t('pipeline.uploadSuccessCount', { count: uploaded.length }));
      } else if (uploaded.length === 0) {
        setMessage(t('pipeline.uploadFailed'));
      } else {
        setMessage(t('pipeline.uploadPartial', { ok: uploaded.length, failed: failed.length }));
      }
    } catch {
      setMessage(t('pipeline.uploadFailed'));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const toggleReqExpansion = (id: number) => {
    setExpandedReqIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleAnalyze = async (attachmentIds?: number[], force = false) => {
    if (!caseId || submittingAnalysis) return;
    if (analysisRunning && !force) return;
    setSubmittingAnalysis(true);
    setMessage(t('pipeline.analyzeStarting'));
    try {
      await pipelineService.startAnalysis(caseId, attachmentIds, { force });
      await refetchCase();
      setMessage(t('pipeline.analyzeStartedBackground'));
    } catch (err: unknown) {
      const axiosErr = err as {
        response?: { data?: { error?: string; code?: string } };
        message?: string;
      };
      const backendMsg = axiosErr.response?.data?.error;
      if (axiosErr.response?.data?.code === 'ANALYSIS_IN_PROGRESS') {
        await refetchCase();
        setMessage(t('pipeline.analyzeAlreadyRunningInfo'));
      } else if (backendMsg) {
        setMessage(backendMsg);
      } else {
        setMessage(err instanceof Error ? err.message : t('pipeline.analyzeFailed'));
      }
    } finally {
      setSubmittingAnalysis(false);
    }
  };

  const toggleDocSelection = (id: number) => {
    setSelectedDocIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleAnalyzeSelected = () => {
    if (selectedDocIds.length === 0) return;
    handleAnalyze(selectedDocIds);
  };

  const handleCreateDevTask = async (req: PipelineRequirement) => {
    try {
      const result = await pipelineService.createDevTask(req.id, { create_project: true });
      refetchReqs();
      setMessage(t('pipeline.devTaskCreated'));
      if ((result as { dev_project_id?: number })?.dev_project_id) {
        navigate(`/admin/dev-projects/${(result as { dev_project_id: number }).dev_project_id}`);
      }
    } catch (err: unknown) {
      setMessage(err instanceof Error ? err.message : t('common.error'));
    }
  };

  const getHandlingDraft = (r: PipelineRequirement): HandlingDraft =>
    handlingDrafts[r.id] ?? {
      handling_status: r.handling_status ?? '',
      manual_comment: r.manual_comment ?? '',
      match_status: r.match_status,
      reuse_in_other_cases: false,
    };

  const updateHandlingDraft = (r: PipelineRequirement, patch: Partial<HandlingDraft>) => {
    setHandlingDrafts((prev) => {
      const base = prev[r.id] ?? getHandlingDraft(r);
      return { ...prev, [r.id]: { ...base, ...patch } };
    });
  };

  const handleSaveHandling = async (r: PipelineRequirement) => {
    const draft = getHandlingDraft(r);
    setSavingReqId(r.id);
    try {
      await pipelineService.updateRequirement(r.id, {
        handling_status: (draft.handling_status || null) as PipelineRequirement['handling_status'],
        manual_comment: draft.manual_comment,
        match_status: draft.match_status,
        reuse_in_other_cases: draft.reuse_in_other_cases,
      });
      await refetchReqs();
      setHandlingDrafts((prev) => {
        const next = { ...prev };
        delete next[r.id];
        return next;
      });
      setMessage(
        draft.reuse_in_other_cases ? t('pipeline.handlingSavedReuse') : t('pipeline.handlingSaved')
      );
    } catch (err: unknown) {
      setMessage(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setSavingReqId(null);
    }
  };

  const handleAddValueLine = async () => {
    if (!caseId || !selectedProduct) return;
    const product = (products || []).find((p) => p.id === selectedProduct);
    if (!product) return;
    await pipelineService.addValueLine(caseId, {
      product_id: product.id,
      product_name: product.name,
      product_code: product.code,
      pricing_type: product.pricing_type as PipelineValueLine['pricing_type'],
      unit_price: product.price,
      quantity: parseFloat(quantity) || 1,
      unit: product.unit,
    });
    refetchValue();
    refetchCase();
    setSelectedProduct('');
    setQuantity('1');
  };

  const analysisStatusLabel = (status?: string) => {
    if (!status || status === 'none') return t('pipeline.analysisStatusNone');
    const key = `pipeline.analysisStatusValues.${status}` as const;
    return t(key);
  };

  if (!pipelineCase && caseId) {
    return <div className="p-4">{t('common.loading')}</div>;
  }

  if (!pipelineCase) {
    return <div className="p-4">{t('pipeline.caseNotFound')}</div>;
  }

  const tabs: { key: Tab; label: string; icon: React.ReactNode }[] = [
    { key: 'overview', label: t('pipeline.tabOverview'), icon: <ClipboardDocumentListIcon className="w-4 h-4" /> },
    { key: 'documents', label: t('pipeline.tabDocuments'), icon: <DocumentArrowUpIcon className="w-4 h-4" /> },
    { key: 'requirements', label: t('pipeline.tabRequirements'), icon: <SparklesIcon className="w-4 h-4" /> },
    { key: 'value', label: t('pipeline.tabValue'), icon: <CurrencyDollarIcon className="w-4 h-4" /> },
  ];

  return (
    <div className="p-4 bg-bgContainer h-full overflow-auto">
      <div className="flex items-center gap-3 mb-4">
        <button onClick={() => navigate(ROUTES.CRM_PIPELINE)} className="p-2 hover:bg-bgElevated rounded-lg">
          <ArrowLeftIcon className="w-5 h-5 text-textDefault" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-textDefault">{pipelineCase.title}</h1>
          <p className="text-sm text-textSecondary">
            {pipelineCase.stage_name} · {pipelineCase.case_type === 'procurement' ? t('pipeline.typeProcurement') : t('pipeline.typeInquiry')}
          </p>
        </div>
      </div>

      {message && <div className="mb-4 p-3 rounded-lg bg-bgElevated border border-border text-sm">{message}</div>}
      {analysisRunning && (
        <div className="mb-4 p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-900 flex flex-wrap items-center justify-between gap-3">
          <span>{t('pipeline.analysisRunningHint')}</span>
          <button
            type="button"
            onClick={() => handleAnalyze(undefined, true)}
            className="px-3 py-1.5 bg-blue-800 text-white rounded-lg text-sm shrink-0"
          >
            {t('pipeline.forceRestartAnalysis')}
          </button>
        </div>
      )}

      <div className="flex gap-2 border-b border-border mb-4">
        {tabs.map((item) => (
          <button
            key={item.key}
            onClick={() => setTab(item.key)}
            className={`flex items-center gap-2 px-4 py-2 text-sm border-b-2 -mb-px ${
              tab === item.key ? 'border-primary text-primary' : 'border-transparent text-textSecondary'
            }`}
          >
            {item.icon}
            {item.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-bgElevated rounded-xl border border-border p-4 space-y-2">
            <h2 className="font-semibold">{t('pipeline.tabOverview')}</h2>
            <p><span className="text-textSecondary">{t('crm.customer')}:</span> {pipelineCase.customer_name || '–'}</p>
            <p><span className="text-textSecondary">{t('pipeline.contact')}:</span> {pipelineCase.contact_name || '–'}</p>
            <p><span className="text-textSecondary">{t('pipeline.estimatedValue')}:</span> {formatCurrency(pipelineCase.estimated_value, pipelineCase.currency)}</p>
            <p><span className="text-textSecondary">{t('pipeline.calculatedValue')}:</span> {formatCurrency(pipelineCase.calculated_value, pipelineCase.currency)}</p>
            <p><span className="text-textSecondary">{t('pipeline.analysisStatus')}:</span> {analysisStatusLabel(pipelineCase.analysis_status)}</p>
            {pipelineCase.analysis_status === 'failed' && pipelineCase.analysis_error_message && (
              <p className="text-sm text-red-600">{pipelineCase.analysis_error_message}</p>
            )}
            {pipelineCase.description && <p className="text-sm text-textSecondary whitespace-pre-wrap">{pipelineCase.description}</p>}
          </div>
          {pipelineCase.case_type === 'procurement' && (
            <div className="bg-bgElevated rounded-xl border border-border p-4 space-y-2">
              <h2 className="font-semibold">{t('pipeline.typeProcurement')}</h2>
              <p><span className="text-textSecondary">{t('pipeline.referenceNumber')}:</span> {pipelineCase.reference_number || '–'}</p>
              <p><span className="text-textSecondary">{t('pipeline.submissionDeadline')}:</span> {pipelineCase.submission_deadline ? new Date(pipelineCase.submission_deadline).toLocaleDateString('sv-SE') : '–'}</p>
              {pipelineCase.procurement_url && (
                <a href={pipelineCase.procurement_url} target="_blank" rel="noreferrer" className="text-primary text-sm underline">
                  {t('pipeline.procurementUrl')}
                </a>
              )}
            </div>
          )}
        </div>
      )}

      {tab === 'documents' && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <label className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg cursor-pointer">
              <DocumentArrowUpIcon className="w-4 h-4" />
              {uploading ? t('common.uploading') : t('pipeline.uploadDocument')}
              <input type="file" multiple className="hidden" onChange={handleUpload} disabled={uploading} />
            </label>
            <span className="text-xs text-textTertiary">{t('pipeline.uploadMultipleHint')}</span>
          </div>
          <div className="space-y-2">
            {(attachments || []).map((a) => (
              <div key={a.id} className="flex items-center justify-between p-3 bg-bgElevated rounded-lg border border-border gap-3">
                <label className="flex items-center gap-3 min-w-0 flex-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedDocIds.includes(a.id)}
                    onChange={() => toggleDocSelection(a.id)}
                    className="rounded border-border"
                  />
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{a.original_filename}</div>
                    <div className="text-xs text-textTertiary">{a.document_type} · {a.uploader_name}</div>
                  </div>
                </label>
                <div className="flex items-center gap-3 shrink-0">
                  <button
                    type="button"
                    onClick={() => handleAnalyze([a.id])}
                    disabled={analysisRunning || submittingAnalysis}
                    className="inline-flex items-center gap-1 text-primary text-sm disabled:opacity-50"
                  >
                    <SparklesIcon className="w-4 h-4" />
                    {analysisRunning ? t('pipeline.analyzing') : t('pipeline.analyzeDocument')}
                  </button>
                  <a
                    href={pipelineService.getAttachmentFileUrl(caseId, a.id)}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary text-sm"
                  >
                    {t('common.download')}
                  </a>
                </div>
              </div>
            ))}
            {(attachments || []).length === 0 && <p className="text-textSecondary text-sm">{t('pipeline.noDocuments')}</p>}
          </div>
        </div>
      )}

      {tab === 'requirements' && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2 items-center">
            <button
              onClick={() => handleAnalyze()}
              disabled={analysisRunning || submittingAnalysis || (attachments || []).length === 0}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg disabled:opacity-50"
            >
              <SparklesIcon className="w-4 h-4" />
              {analysisRunning ? t('pipeline.analyzing') : t('pipeline.runAnalysisAll')}
            </button>
            <button
              onClick={handleAnalyzeSelected}
              disabled={analysisRunning || submittingAnalysis || selectedDocIds.length === 0}
              className="inline-flex items-center gap-2 px-4 py-2 border border-border rounded-lg disabled:opacity-50"
            >
              <SparklesIcon className="w-4 h-4" />
              {t('pipeline.runAnalysisSelected')}
            </button>
            {reqData?.summary && (
              <span className="text-sm text-textSecondary">
                {reqData.summary.fulfilled}/{reqData.summary.total} {t('pipeline.fulfilled')}
              </span>
            )}
          </div>
          {reqData?.summary && reqData.summary.total > 0 && (
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-800">
                {t('pipeline.skaCount', { count: reqData.summary.ska_total })}
              </span>
              <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700">
                {t('pipeline.borCount', { count: reqData.summary.bor_total })}
              </span>
              <span className="px-2 py-0.5 rounded bg-green-100 text-green-800">
                {t('pipeline.status.fulfilled')}: {reqData.summary.fulfilled}
              </span>
              <span className="px-2 py-0.5 rounded bg-yellow-100 text-yellow-800">
                {t('pipeline.status.partial')}: {reqData.summary.partial}
              </span>
              <span className="px-2 py-0.5 rounded bg-red-100 text-red-800">
                {t('pipeline.status.missing')}: {reqData.summary.missing}
              </span>
              <span className="px-2 py-0.5 rounded bg-purple-100 text-purple-800">
                {t('pipeline.status.review')}: {reqData.summary.review}
              </span>
            </div>
          )}
          <p className="text-xs text-textTertiary">{t('pipeline.runAnalysisAllHint')}</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-textSecondary">
                  <th className="py-2 pr-2 w-8"></th>
                  <th className="py-2 pr-2">{t('pipeline.requirementType')}</th>
                  <th className="py-2 pr-2">{t('pipeline.requirement')}</th>
                  <th className="py-2 pr-2">{t('pipeline.matchStatus')}</th>
                  <th className="py-2 pr-2">{t('pipeline.handlingStatus')}</th>
                  <th className="py-2 pr-2">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {(reqData?.requirements || []).map((r) => {
                  const expanded = expandedReqIds.includes(r.id);
                  return (
                    <React.Fragment key={r.id}>
                      <tr
                        className="border-b border-border/50 cursor-pointer hover:bg-bgElevated/60"
                        onClick={() => toggleReqExpansion(r.id)}
                      >
                        <td className="py-2 pr-2 text-textTertiary">
                          <ChevronRightIcon className={`w-4 h-4 transition-transform ${expanded ? 'rotate-90' : ''}`} />
                        </td>
                        <td className="py-2 pr-2">
                          <span className={`px-1.5 py-0.5 rounded text-xs font-semibold uppercase ${r.requirement_type === 'ska' ? 'bg-blue-100 text-blue-800' : 'bg-slate-100 text-slate-700'}`}>
                            {r.requirement_type}
                          </span>
                        </td>
                        <td className="py-2 pr-2">
                          {r.requirement_ref && <span className="text-textTertiary mr-1">{r.requirement_ref}</span>}
                          <span className="font-medium">{r.title}</span>
                        </td>
                        <td className="py-2 pr-2">
                          <span className={`px-2 py-0.5 rounded text-xs ${statusColors[r.match_status] || statusColors.unknown}`}>
                            {t(`pipeline.status.${r.match_status}`)}
                          </span>
                        </td>
                        <td className="py-2 pr-2">
                          {r.handling_status ? (
                            <span className={`px-2 py-0.5 rounded text-xs ${handlingColors[r.handling_status] || statusColors.unknown}`}>
                              {t(`pipeline.handling.${r.handling_status}`)}
                            </span>
                          ) : (
                            <span className="text-xs text-textTertiary">{t('pipeline.handlingNotSet')}</span>
                          )}
                        </td>
                        <td className="py-2 pr-2" onClick={(e) => e.stopPropagation()}>
                          {['partial', 'missing'].includes(r.match_status) && !r.dev_task_id && (
                            <button onClick={() => handleCreateDevTask(r)} className="text-primary text-xs underline">
                              {t('pipeline.createDevTask')}
                            </button>
                          )}
                          {r.dev_task_id && <span className="text-xs text-green-700">#{r.dev_task_id}</span>}
                        </td>
                      </tr>
                      {expanded && (
                        <tr className="border-b border-border/50 bg-bgElevated/40">
                          <td></td>
                          <td colSpan={5} className="py-3 pr-4 space-y-3">
                            <div>
                              <div className="text-xs font-semibold text-textSecondary mb-1">{t('pipeline.requirementText')}</div>
                              <div className="text-sm whitespace-pre-wrap">{r.description || r.title}</div>
                            </div>
                            <div className="grid md:grid-cols-2 gap-3">
                              <div className="bg-green-50 border border-green-100 rounded-lg p-3">
                                <div className="text-xs font-semibold text-green-800 mb-1">{t('pipeline.systemHandles')}</div>
                                {r.coverage_notes ? (
                                  <div className="text-sm text-green-900 whitespace-pre-wrap">{r.coverage_notes}</div>
                                ) : (
                                  <div className="text-sm text-textTertiary">{t('pipeline.noCoverageNotes')}</div>
                                )}
                                <div className="text-xs text-green-700 mt-2">
                                  {r.catalog_title && (
                                    <div>
                                      {t('pipeline.matchedCapability')}: {r.catalog_title}
                                      {r.catalog_code && <span> ({r.catalog_code})</span>}
                                    </div>
                                  )}
                                  <div>
                                    {t('pipeline.matchStatus')}: {t(`pipeline.status.${r.match_status}`)}
                                    {typeof r.match_confidence === 'number' && (
                                      <span> · {t('pipeline.confidence')}: {Math.round(r.match_confidence * 100)}%</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="bg-amber-50 border border-amber-100 rounded-lg p-3">
                                <div className="text-xs font-semibold text-amber-800 mb-1">{t('pipeline.whatIsMissing')}</div>
                                <div className="text-sm text-amber-900 whitespace-pre-wrap">
                                  {r.gap_notes || (r.match_status === 'fulfilled' ? t('pipeline.nothingMissing') : t('pipeline.noGapNotes'))}
                                </div>
                              </div>
                            </div>
                            <div className="bg-bgElevated border border-border rounded-lg p-3 space-y-3">
                              <div className="text-xs font-semibold text-textSecondary">{t('pipeline.manualHandling')}</div>
                              <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                                <div className="flex-shrink-0">
                                  <label className="block text-xs text-textTertiary mb-1">{t('pipeline.assessment')}</label>
                                  <select
                                    value={getHandlingDraft(r).match_status}
                                    onChange={(e) => updateHandlingDraft(r, { match_status: e.target.value as MatchStatus })}
                                    className="border border-border rounded-lg px-2 py-1.5 text-sm bg-bgBase"
                                  >
                                    {MATCH_OPTIONS.map((opt) => (
                                      <option key={opt} value={opt}>{t(`pipeline.status.${opt}`)}</option>
                                    ))}
                                  </select>
                                </div>
                                <div className="flex-shrink-0">
                                  <label className="block text-xs text-textTertiary mb-1">{t('pipeline.handlingStatus')}</label>
                                  <select
                                    value={getHandlingDraft(r).handling_status}
                                    onChange={(e) => updateHandlingDraft(r, { handling_status: e.target.value })}
                                    className="border border-border rounded-lg px-2 py-1.5 text-sm bg-bgBase"
                                  >
                                    <option value="">{t('pipeline.handlingNotSet')}</option>
                                    {HANDLING_OPTIONS.map((opt) => (
                                      <option key={opt} value={opt}>{t(`pipeline.handling.${opt}`)}</option>
                                    ))}
                                  </select>
                                </div>
                                <div className="flex-1">
                                  <label className="block text-xs text-textTertiary mb-1">{t('pipeline.comment')}</label>
                                  <textarea
                                    value={getHandlingDraft(r).manual_comment}
                                    onChange={(e) => updateHandlingDraft(r, { manual_comment: e.target.value })}
                                    rows={2}
                                    placeholder={t('pipeline.commentPlaceholder')}
                                    className="w-full border border-border rounded-lg px-2 py-1.5 text-sm bg-bgBase"
                                  />
                                </div>
                                <div className="flex-shrink-0">
                                  <button
                                    onClick={() => handleSaveHandling(r)}
                                    disabled={savingReqId === r.id}
                                    className="px-3 py-1.5 rounded-lg bg-primary text-white text-sm disabled:opacity-50"
                                  >
                                    {savingReqId === r.id ? t('common.saving') : t('common.save')}
                                  </button>
                                </div>
                              </div>
                              <label className="flex items-start gap-2 text-sm text-textSecondary cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={getHandlingDraft(r).reuse_in_other_cases}
                                  onChange={(e) => updateHandlingDraft(r, { reuse_in_other_cases: e.target.checked })}
                                  className="mt-0.5"
                                />
                                <span>
                                  {t('pipeline.reuseInOtherCases')}
                                  <span className="block text-xs text-textTertiary">{t('pipeline.reuseInOtherCasesHint')}</span>
                                </span>
                              </label>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
            {(reqData?.requirements || []).length === 0 && (
              <p className="text-textSecondary text-sm py-4">{t('pipeline.noRequirements')}</p>
            )}
          </div>
        </div>
      )}

      {tab === 'value' && (
        <div className="space-y-4">
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-bgElevated rounded-xl border border-border p-4">
              <div className="text-xs text-textTertiary">{t('pipeline.calculatedValue')}</div>
              <div className="text-xl font-bold">{formatCurrency(valueSummary?.calculated_value, pipelineCase.currency)}</div>
            </div>
            <div className="bg-bgElevated rounded-xl border border-border p-4">
              <div className="text-xs text-textTertiary">{t('pipeline.monthlyTotal')}</div>
              <div className="text-xl font-bold">{formatCurrency(valueSummary?.monthly_total, pipelineCase.currency)}</div>
            </div>
            <div className="bg-bgElevated rounded-xl border border-border p-4">
              <div className="text-xs text-textTertiary">{t('pipeline.estimatedValue')}</div>
              <div className="text-xl font-bold">{formatCurrency(valueSummary?.estimated_value, pipelineCase.currency)}</div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 items-end bg-bgElevated p-4 rounded-xl border border-border">
            <div>
              <label className="block text-xs mb-1">{t('pipeline.product')}</label>
              <select
                value={selectedProduct}
                onChange={(e) => setSelectedProduct(e.target.value)}
                className="px-3 py-2 rounded-lg border border-border bg-bgContainer text-sm min-w-[200px]"
              >
                <option value="">{t('pipeline.selectProduct')}</option>
                {(products || []).map((p) => (
                  <option key={p.id} value={p.id}>{p.name} ({formatCurrency(p.price)})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs mb-1">{t('pipeline.quantity')}</label>
              <input
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                className="w-24 px-3 py-2 rounded-lg border border-border bg-bgContainer text-sm"
              />
            </div>
            <button onClick={handleAddValueLine} disabled={!selectedProduct} className="px-4 py-2 bg-primary text-white rounded-lg text-sm disabled:opacity-50">
              {t('pipeline.addLine')}
            </button>
          </div>

          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-textSecondary">
                <th className="py-2">{t('pipeline.product')}</th>
                <th className="py-2">{t('pipeline.quantity')}</th>
                <th className="py-2">{t('pipeline.unitPrice')}</th>
                <th className="py-2">{t('pipeline.lineTotal')}</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {(valueSummary?.lines || []).map((line: PipelineValueLine) => (
                <tr key={line.id} className="border-b border-border/50">
                  <td className="py-2">{line.product_name}</td>
                  <td className="py-2">{line.quantity} {line.unit}</td>
                  <td className="py-2">{formatCurrency(line.unit_price)}</td>
                  <td className="py-2">{formatCurrency(line.line_total)}</td>
                  <td className="py-2">
                    <button
                      onClick={async () => { await pipelineService.deleteValueLine(caseId, line.id); refetchValue(); refetchCase(); }}
                      className="text-red-600 text-xs"
                    >
                      {t('common.delete')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
